import React, { useEffect, useState } from "react";
import Layout from "../../../Component/Layout"; // adjust path to match your project structure
import axios from "axios";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MASTER_API, PURCHASE_API } from "../../../apiConfig"; // adjust if GST config lives under a different base

// ── compact react-select styles (same pattern used across the app) ──
const rsCompact = {
  control: (base) => ({
    ...base,
    minHeight: "32px",
    height: "32px",
    fontSize: "13px",
    borderColor: "#ced4da",
    boxShadow: "none",
    "&:hover": { borderColor: "#86b7fe" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: "32px" }),
  menu: (base) => ({ ...base, zIndex: 99999, fontSize: "13px" }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
};

const MOVEMENT_TYPES_FALLBACK = ["Inter State", "Intra State"];

const GST = () => {
  const [movementTypes, setMovementTypes] = useState([]);
  const [hsnCodes, setHsnCodes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [taxConfigList, setTaxConfigList] = useState([]); // master Tax Configuration rows (country + movement_type + tax_type)
  const [percentageOptions, setPercentageOptions] = useState([]); // master Percentage rows, feeds the Percent (%) dropdown
  const [configList, setConfigList] = useState([]);

  // ================= TABLE FILTERS =================
  const [filterCountry, setFilterCountry] = useState("");
  const [filterMovementType, setFilterMovementType] = useState("");
  const [filterHsnCode, setFilterHsnCode] = useState("");

  // ================= MODAL / FORM STATE =================
  const [showModal, setShowModal] = useState(false);
  const [movementType, setMovementType] = useState("");
  const [hsnCode, setHsnCode] = useState("");
  const [country, setCountry] = useState("");
  const [taxRows, setTaxRows] = useState([]);
  const [editingId, setEditingId] = useState(null);

  // ================= INITIAL LOAD =================
  useEffect(() => {
    refreshList();

    axios.get(`${MASTER_API}/configuration/dynamic/movement-type`)
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setMovementTypes(list.length ? list : MOVEMENT_TYPES_FALLBACK.map((n, i) => ({ id: i, name: n })));
      })
      .catch(err => {
        console.error("Movement type error:", err);
        setMovementTypes(MOVEMENT_TYPES_FALLBACK.map((n, i) => ({ id: i, name: n })));
      });

    axios.get(`${MASTER_API}/configuration/hsn-code`)
      .then(res => setHsnCodes(Array.isArray(res.data) ? res.data : res.data?.data || []))
      .catch(err => console.error("HSN code error:", err));

    axios.get(`${MASTER_API}/configuration/country`)
      .then(res => setCountries(Array.isArray(res.data) ? res.data : res.data?.data || []))
      .catch(err => console.error("Country error:", err));

    // Master Tax Configuration (Country + Movement Type + Tax Type) — used to
    // auto-fill the tax rows below instead of letting the user add them by hand.
    axios.get(`${MASTER_API}/configuration/tax-config`)
      .then(res => setTaxConfigList(Array.isArray(res.data) ? res.data : res.data?.data || []))
      .catch(err => console.error("Tax config error:", err));

    // Percentage master — feeds the Percent (%) dropdown for each tax row.
    axios.get(`${MASTER_API}/configuration/percentage-config`)
      .then(res => setPercentageOptions(Array.isArray(res.data) ? res.data : res.data?.data || []))
      .catch(err => console.error("Percentage config error:", err));
  }, []);

  // ================= AUTO-FILL TAXES FROM TAX CONFIGURATION =================
  // Whenever Country + Movement Type are both selected, pull the tax types
  // configured for that combination (Configuration > Tax Configuration) and
  // use them as the tax rows for this GST rule. Percent still has to be
  // entered here since the master only stores the tax type, not the rate.
  useEffect(() => {
    if (!country || !movementType) return;

    const matches = taxConfigList.filter(
      (t) => t.country === country && t.movement_type === movementType
    );

    setTaxRows((prev) =>
      matches.map((m) => {
        const existing = prev.find((p) => p.tax_type === m.tax_type);
        return { tax_type: m.tax_type, percent: existing ? existing.percent : "" };
      })
    );
  }, [country, movementType, taxConfigList]);

  const refreshList = () => {
    axios.get(`${PURCHASE_API}/gst-config`)
      .then(res => setConfigList(Array.isArray(res.data) ? res.data : res.data?.data || []))
      .catch(err => console.error("GST config list error:", err));
  };

  // ================= FILTERED LIST (Country / Movement Type / HSN Code) =================
  const filteredConfigList = configList.filter((row) => {
    if (filterCountry && row.country !== filterCountry) return false;
    if (filterMovementType && row.movement_type !== filterMovementType) return false;
    if (filterHsnCode && row.hsn_code !== filterHsnCode) return false;
    return true;
  });

  const clearFilters = () => {
    setFilterCountry("");
    setFilterMovementType("");
    setFilterHsnCode("");
  };

  // ================= CREATE (opens blank modal) =================
  const openCreate = () => {
    setMovementType("");
    setHsnCode("");
    setCountry("");
    setTaxRows([]);
    setEditingId(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setMovementType("");
    setHsnCode("");
    setCountry("");
    setTaxRows([]);
    setEditingId(null);
  };

  // ================= TAX ROWS =================
  // Rows are auto-filled from Tax Configuration (see the useEffect above) once
  // Country + Movement Type are picked — there's no manual "+ Tax" add anymore.
  // A row can still be removed here if a particular tax shouldn't apply.
  const updateTaxRow = (idx, field, value) => {
    setTaxRows(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const removeTaxRow = (idx) => {
    setTaxRows(prev => prev.filter((_, i) => i !== idx));
  };

  // ================= SAVE =================
  const handleSave = async () => {
    if (!country) return toast.warning("Country is mandatory");
    if (!movementType) return toast.warning("Movement Type is mandatory");
    if (!hsnCode) return toast.warning("HSN Code is mandatory");

    const validTaxes = taxRows.filter(t => t.tax_type && t.percent !== "");
    if (taxRows.length > 0 && validTaxes.length !== taxRows.length) {
      return toast.warning("Enter a Percent for every tax type, or remove the row");
    }

    // Only one GST configuration allowed per Country + Movement Type + HSN Code
    const isDuplicate = configList.some(row =>
      row.country === country &&
      row.movement_type === movementType &&
      row.hsn_code === hsnCode &&
      row.id !== editingId
    );
    if (isDuplicate) {
      return toast.warning(`A GST configuration already exists for ${country} / ${movementType} / HSN ${hsnCode}. Edit the existing entry instead.`);
    }

    const payload = {
      country,
      movement_type: movementType,
      hsn_code: hsnCode,
      taxes: validTaxes,
    };

    try {
      if (editingId) {
        await axios.put(`${PURCHASE_API}/gst-config/${editingId}`, payload);
        toast.success("GST configuration updated");
      } else {
        await axios.post(`${PURCHASE_API}/gst-config`, payload);
        toast.success("GST configuration saved");
      }
      closeModal();
      refreshList();
    } catch (err) {
      console.error("Save GST config error:", err);
      toast.error(err?.response?.data?.error || "Failed to save GST configuration");
    }
  };

  // ================= EDIT / DELETE =================
  const openEdit = (row) => {
    setCountry(row.country || "");
    setMovementType(row.movement_type);
    setHsnCode(row.hsn_code);
    setTaxRows(row.taxes && row.taxes.length ? row.taxes.map(t => ({ tax_type: t.tax_type, percent: String(t.percent) })) : []);
    setEditingId(row.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this GST configuration?")) return;
    try {
      await axios.delete(`${PURCHASE_API}/gst-config/${id}`);
      toast.success("Deleted");
      refreshList();
    } catch (err) {
      console.error("Delete GST config error:", err);
      toast.error("Failed to delete");
    }
  };

  return (
    <Layout>
      <style>{`
        .gst-wrapper { padding: 32px; font-family: Inter, Poppins, sans-serif; }
        .gst-header-row { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
        .gst-header h1 { font-size: 24px; font-weight: 700; color: #0b1f4d; margin: 0 0 6px 0; }
        .gst-header p { font-size: 14px; color: #6b7280; margin: 0; }
        .gst-create-btn {
          background: #0057d9; color: #fff; border: none; border-radius: 8px;
          font-size: 13px; font-weight: 600; padding: 9px 18px; cursor: pointer; white-space: nowrap;
        }
        .gst-create-btn:hover { background: #0046ad; }

        .gst-filter-card {
          background: #ffffff; border: 1px solid #e5e7eb; border-radius: 16px;
          padding: 20px; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(16,24,40,0.06);
        }
        .gst-filter-label { font-size: 12px; font-weight: 600; color: #334155; margin-bottom: 4px; display: block; }
        .gst-clear-filters-btn {
          background: none; border: 1px solid #ced4da; border-radius: 8px; color: #475569;
          font-size: 12px; font-weight: 600; padding: 0 12px; height: 32px; cursor: pointer;
        }
        .gst-clear-filters-btn:hover { background: #f1f5f9; }

        .gst-table-card {
          background: #ffffff; border: 1px solid #e5e7eb; border-radius: 16px;
          padding: 20px; box-shadow: 0 1px 3px rgba(16,24,40,0.06); overflow-x: auto;
        }
        .gst-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .gst-table th {
          text-align: left; background: #f8fafc; font-weight: 600; color: #1e293b;
          padding: 10px 12px; border-bottom: 1px solid #e5e7eb; white-space: nowrap;
        }
        .gst-table td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; color: #334155; vertical-align: middle; }
        .gst-tax-chip {
          display: inline-block; background: #eef2ff; color: #4338ca; font-size: 11px;
          font-weight: 600; padding: 2px 8px; border-radius: 999px; margin: 2px 4px 2px 0;
        }

        .gst-modal-backdrop {
          position: fixed; inset: 0; background: rgba(0,0,0,0.45); display: flex;
          align-items: center; justify-content: center; z-index: 1050; padding: 20px;
        }
        .gst-modal {
          background: #fff; border-radius: 12px; width: 95%; max-width: 640px;
          max-height: 90vh; display: flex; flex-direction: column; overflow: hidden;
        }
        .gst-modal-header {
          padding: 16px 20px; border-bottom: 1px solid #e5e7eb; display: flex;
          justify-content: space-between; align-items: center;
        }
        .gst-modal-title { font-size: 17px; font-weight: 700; color: #0b1f4d; }
        .gst-modal-header-actions { display: flex; align-items: center; gap: 10px; }
        .gst-add-tax-btn {
          background: #0057d9; color: #fff; border: none; border-radius: 8px;
          font-size: 12px; font-weight: 600; padding: 6px 12px; cursor: pointer;
        }
        .gst-close-btn { background: none; border: none; font-size: 18px; cursor: pointer; color: #64748b; }
        .gst-modal-body { padding: 20px; overflow-y: auto; flex: 1; }
        .gst-field-label { font-size: 12px; font-weight: 600; color: #334155; margin-bottom: 4px; display: block; }
        .gst-form-row { display: flex; gap: 14px; }
        .gst-form-row > div { flex: 1; }
        .gst-tax-row {
          display: flex; gap: 10px; align-items: flex-end; margin-top: 10px;
        }
        .gst-tax-row-remove {
          background: none; border: none; color: #dc2626; font-size: 16px;
          cursor: pointer; padding: 4px 6px; height: 32px;
        }
        .gst-modal-footer {
          padding: 14px 20px; border-top: 1px solid #e5e7eb; display: flex;
          justify-content: flex-end; gap: 10px;
        }
      `}</style>

      <div className="gst-wrapper">
        <div className="gst-header-row">
          <div className="gst-header">
            <h1>GST</h1>
            <p>GST configuration and compliance settings</p>
          </div>
          <button className="gst-create-btn" onClick={openCreate}>+ Create</button>
        </div>

        {/* ================= FILTERS: COUNTRY + MOVEMENT TYPE + HSN CODE ================= */}
        <div className="gst-filter-card">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="gst-filter-label">Country</label>
              <Select
                styles={rsCompact}
                placeholder="Select Country"
                isClearable
                options={countries.map(c => ({ value: c.name || c, label: c.name || c }))}
                value={filterCountry ? { value: filterCountry, label: filterCountry } : null}
                onChange={(opt) => setFilterCountry(opt?.value || "")}
              />
            </div>

            <div className="col-md-3">
              <label className="gst-filter-label">Movement Type</label>
              <Select
                styles={rsCompact}
                placeholder="Select Movement Type"
                isClearable
                options={movementTypes.map(m => ({ value: m.name || m, label: m.name || m }))}
                value={filterMovementType ? { value: filterMovementType, label: filterMovementType } : null}
                onChange={(opt) => setFilterMovementType(opt?.value || "")}
              />
            </div>

            <div className="col-md-3">
              <label className="gst-filter-label">HSN Code</label>
              <Select
                styles={rsCompact}
                placeholder="Select HSN Code"
                isClearable
                options={hsnCodes.map(h => ({ value: h.hsn_code, label: h.hsn_code }))}
                value={filterHsnCode ? { value: filterHsnCode, label: filterHsnCode } : null}
                onChange={(opt) => setFilterHsnCode(opt?.value || "")}
              />
            </div>

            <div className="col-md-3">
              <button className="gst-clear-filters-btn" onClick={clearFilters}>Clear Filters</button>
            </div>
          </div>
        </div>

        {/* ================= SAVED GST RULES LIST ================= */}
        <div className="gst-table-card">
          <table className="gst-table">
            <thead>
              <tr>
                <th>Country</th>
                <th>Movement Type</th>
                <th>HSN Code</th>
                <th>Taxes</th>
                <th style={{ textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredConfigList.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center", padding: 20, color: "#94a3b8" }}>
                    {configList.length === 0 ? "No GST configuration added yet" : "No GST configuration matches the selected filters"}
                  </td>
                </tr>
              ) : (
                filteredConfigList.map((row) => (
                  <tr key={row.id}>
                    <td>{row.country || "—"}</td>
                    <td>{row.movement_type}</td>
                    <td>{row.hsn_code}</td>
                    <td>
                      {row.taxes && row.taxes.length > 0
                        ? row.taxes.map((t, i) => (
                            <span key={i} className="gst-tax-chip">{t.tax_type} {t.percent}%</span>
                          ))
                        : "—"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button className="btn btn-outline-dark btn-sm py-0 me-2" style={{ fontSize: 12 }} onClick={() => openEdit(row)}>
                        Edit
                      </button>
                      <button className="btn btn-outline-danger btn-sm py-0" style={{ fontSize: 12 }} onClick={() => handleDelete(row.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================= CREATE / EDIT MODAL ================= */}
      {showModal && (
        <div className="gst-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="gst-modal">
            <div className="gst-modal-header">
              <div className="gst-modal-title">
                {editingId ? "Edit GST Configuration" : "Create GST Configuration"}
              </div>
              <div className="gst-modal-header-actions">
                <button className="gst-close-btn" onClick={closeModal}>✕</button>
              </div>
            </div>

            <div className="gst-modal-body">
              <div className="gst-form-row">
                <div>
                  <label className="gst-field-label">Country *</label>
                  <Select
                    styles={rsCompact}
                    placeholder="Select Country"
                    options={countries.map(c => ({ value: c.name || c, label: c.name || c }))}
                    value={country ? { value: country, label: country } : null}
                    onChange={(opt) => setCountry(opt?.value || "")}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuShouldBlockScroll
                  />
                </div>

                <div>
                  <label className="gst-field-label">Movement Type *</label>
                  <Select
                    styles={rsCompact}
                    placeholder="Select Movement Type"
                    options={movementTypes.map(m => ({ value: m.name || m, label: m.name || m }))}
                    value={movementType ? { value: movementType, label: movementType } : null}
                    onChange={(opt) => setMovementType(opt?.value || "")}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuShouldBlockScroll
                  />
                </div>
              </div>

              <div style={{ marginTop: 14 }}>
                <label className="gst-field-label">HSN Code *</label>
                <Select
                  styles={rsCompact}
                  placeholder="Select HSN Code"
                  options={hsnCodes.map(h => ({ value: h.hsn_code, label: h.hsn_code }))}
                  value={hsnCode ? { value: hsnCode, label: hsnCode } : null}
                  onChange={(opt) => setHsnCode(opt?.value || "")}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuShouldBlockScroll
                />
              </div>

              {taxRows.map((row, idx) => (
                <div className="gst-tax-row" key={idx}>
                  <div style={{ flex: 1 }}>
                    <label className="gst-field-label">Tax Type</label>
                    <input
                      className="form-control"
                      style={{ fontSize: 13, height: 32, padding: "4px 8px", background: "#f1f5f9" }}
                      value={row.tax_type}
                      readOnly
                      title="Comes from Configuration > Tax Configuration for this Country + Movement Type"
                    />
                  </div>
                  <div style={{ width: 140 }}>
                    <label className="gst-field-label">Percent (%) *</label>
                    <Select
                      styles={rsCompact}
                      placeholder="Select %"
                      options={percentageOptions.map(p => ({ value: String(p.percent), label: `${p.percent}%` }))}
                      value={row.percent !== "" ? { value: String(row.percent), label: `${row.percent}%` } : null}
                      onChange={(opt) => updateTaxRow(idx, "percent", opt?.value || "")}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      menuShouldBlockScroll
                    />
                  </div>
                  <button className="gst-tax-row-remove" onClick={() => removeTaxRow(idx)} title="Remove this tax">
                    ✕
                  </button>
                </div>
              ))}

              {taxRows.length === 0 && (
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 12 }}>
                  {country && movementType
                    ? "No taxes are configured for this Country + Movement Type in Configuration > Tax Configuration."
                    : "Select a Country and Movement Type to pull in the tax types from Configuration > Tax Configuration."}
                </div>
              )}
            </div>

            <div className="gst-modal-footer">
              <button className="btn btn-outline-secondary btn-sm" onClick={closeModal}>Cancel</button>
              <button className="btn btn-dark btn-sm" onClick={handleSave}>Save</button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Layout>
  );
};

export default GST;